import { ChevronDownIcon, ChevronUpIcon, MinusIcon } from "lucide-react-native";
import { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

/**
 * Props for RankChangeIndicatorView
 */
interface RankChangeIndicatorViewProps {
  /**
   * The rank change value (positive = improved, negative = declined, zero = no change)
   */
  rankChange: number;
}

/**
 * Displays a rank change indicator with directional icon and value.
 * Shows a minus icon for zero change, up/down chevron for positive/negative.
 * @remarks
 * This component encapsulates the three-way conditional logic for rank change display:
 * - Zero change: minus icon
 * - Positive change: up chevron + green text with "+" prefix
 * - Negative change: down chevron + orange text with absolute value
 * @param root0 - Component props
 * @param root0.rankChange - Player's rank change value
 */
const RankChangeIndicatorView = ({
  rankChange,
}: RankChangeIndicatorViewProps) =>
  rankChange === 0 ? (
    <Icon
      as={MinusIcon}
      className="text-statusBadge-default-text-400"
      size="md"
    />
  ) : (
    <>
      <Icon
        as={rankChange > 0 ? ChevronUpIcon : ChevronDownIcon}
        className={
          rankChange > 0
            ? "text-statusBadge-success-300"
            : "text-statusBadge-warning-300"
        }
        size="md"
      />
      <Text
        variant="body-strong"
        className={
          rankChange > 0
            ? "text-statusBadge-success-300"
            : "text-statusBadge-warning-300"
        }
      >
        {`${rankChange > 0 ? "+" : ""}${Math.abs(rankChange)}`}
      </Text>
    </>
  );

RankChangeIndicatorView.displayName = "RankChangeIndicatorView";
export default memo(RankChangeIndicatorView);
